import useOutsideListener from '@Hooks/useOutsideListener';
import React, {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';

import Label from '../Label';
import {
	SelectContextProps,
	SelectProps,
	SelectOption,
	SelectOptionProps,
} from './types';
import {
	ContainerArrow,
	StyledContentContainer,
	StyledOptionContainer,
	StyledSelectButton,
	StyledSelectText,
	StyledTextOption,
} from './styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import TextWithOverflow from '@Components/TextWithOverflow';

const SelectContext = createContext({} as SelectContextProps);

function useSelectContext() {
	const context = useContext(SelectContext);
	if (!context) {
		throw new Error('useSelectContext must be used within a SelectContext');
	}
	return context;
}

export default function Select({
	data,
	value,
	name,
	placeholder = 'Select',
	label,
	className = '',
	buttonContainerClassName = '',
	buttonContainerStyle,
	containerClassName = '',
	displayContainer = 'd-flex',
	disabled = false,
	buttonStyle,
	renderOption,
	multiple,
	containerStyle,
	defaultValue,
	onChange,
	onBlur,

	...buttonProps
}: SelectProps) {
	const dropDownRef = useRef<HTMLDivElement>(null);

	const [state, setState] = useState({
		isOpen: false,
		isAbove: false,
		hasValue: false,
	});

	const { isOpen, isAbove, hasValue } = state;

	useEffect(() => {
		console.log(value, 'value');
		if (value === undefined) {
			setState({
				...state,
				hasValue: false,
			});
			return;
		}
		if (value.length > 0) {
			setState({
				...state,
				hasValue: true,
			});
		}
	}, [value]);
	useOutsideListener({
		ref: dropDownRef,
		callback: () => {
			if (!isOpen) return;
			setState({
				...state,
				isOpen: false,
			});
			onBlur?.();
		},
	});

	useEffect(() => {
		const checkPosition = () => {
			if (dropDownRef.current) {
				const rect = dropDownRef.current.getBoundingClientRect();
				setState({
					...state,
					isAbove: rect.top + rect.height / 2 > window.innerHeight / 2 + 100,
				});
			}
		};
		checkPosition();
		window.addEventListener('resize', checkPosition);
		return () => window.removeEventListener('resize', checkPosition);
	}, []);

	const handleOptionClick = (option: SelectOption) => {
		if (disabled) return;

		onBlur?.();

		if (multiple === true) {
			const currentValues = data.filter((item) => value?.includes(item.value));

			const newValues = value?.find((item) => item === option.value)
				? currentValues.filter((item) => item.value !== option.value)
				: [...currentValues, option];

			onChange?.(newValues);
			return;
		}
		onChange?.(option);

		setState({
			...state,
			isOpen: false,
		});
	};

	const renderOptions = () => {
		if (renderOption)
			return data.map((item, index) => renderOption(item, index));
		return data.map((item) => (
			<StyledOptionContainer
				onClick={() => handleOptionClick(item)}
				key={item.value}
			>
				<StyledTextOption>{item.label}</StyledTextOption>
			</StyledOptionContainer>
		));
	};

	const getSelectText = () => {
		if (multiple) {
			const currentValues = data.filter((item) => value?.includes(item.value));
			const text = currentValues.map((item) => item.label).join(', ');

			return text.length > 0 ? (
				<TextWithOverflow maxLines={1} style={{ lineHeight: 1.5 }}>
					{text}
				</TextWithOverflow>
			) : (
				placeholder
			);
		}
		const currentValues = data.find((item) => item.value === value);
		if (currentValues?.label) {
			return (
				<TextWithOverflow maxLines={1} style={{ lineHeight: 1.5 }}>
					{currentValues?.label}
				</TextWithOverflow>
			);
		}
		return placeholder;
	};

	const toggleContentExpanded = () => {
		if (disabled) return;
		setState({
			...state,
			isOpen: !isOpen,
		});
	};

	const _className = className ?? '';

	return (
		<div className={containerClassName} style={containerStyle}>
			{label && label !== '' && <Label text={label} />}
			<div
				className={`align-items-center position-relative ${buttonContainerClassName}`}
				ref={dropDownRef}
				style={{ height: 49, ...buttonContainerStyle }}
				tabIndex={0}
			>
				<ContainerArrow disabled={disabled}>
					<FontAwesomeIcon
						icon={faAngleDown}
						size="lg"
						color="#7E8299"
						className={`fa-icon ${isOpen ? 'rotate' : ''}`}
						onClick={toggleContentExpanded}
					/>
				</ContainerArrow>
				<StyledSelectButton
					contentOrder="reverse"
					className={`w-100 pe-10 ${_className}`}
					variant="light"
					disabled={disabled}
					style={{ height: 49, ...buttonStyle }}
					onClick={toggleContentExpanded}
					hasValue={hasValue}
					type="button"
					{...buttonProps}
				>
					<StyledSelectText
						color={getSelectText() === placeholder ? '#5E627866' : '#5E6278'}
					>
						{getSelectText()}
					</StyledSelectText>
				</StyledSelectButton>
				{isOpen && (
					<StyledContentContainer isAbove={isAbove}>
						<SelectContext.Provider
							value={{
								handleOptionClick,
							}}
						>
							{renderOptions()}
						</SelectContext.Provider>
					</StyledContentContainer>
				)}
			</div>
		</div>
	);
}

function Option({ option, children, disabled, ...rest }: SelectOptionProps) {
	const { handleOptionClick } = useSelectContext();

	return (
		<StyledOptionContainer
			disabled={disabled}
			onClick={() => (disabled ? null : handleOptionClick(option))}
			{...rest}
		>
			{children}
		</StyledOptionContainer>
	);
}

Select.Option = Option;
